import { useNavigate } from 'react-router'
import { useShallow } from 'zustand/react/shallow'
import { Avatar } from '@/components/ui/Avatar'
import { formatDate, formatTime } from '@/lib/utils/time'
import { useRoomStore } from '@/lib/store/room-store'
import { useThreadStore, selectAllThreads, threadHasUnread, type ThreadInfo } from '@/lib/store/thread-store'

interface ThreadsListViewProps {
  onClose: () => void
}

function getSenderDisplay(sender: string): string {
  const match = sender.match(/^@(.+?):/)
  return match?.[1] ?? sender
}

/**
 * Global Threads view: every thread across all rooms, newest activity first.
 * This is what makes earlier threads reachable after you've scrolled past their
 * root in the main timeline. Per-thread unread is independent of room unread.
 *
 * Reads only off the thread store — no new Matrix calls. Opening a thread's own
 * panel (loading replies, composing, read receipts) is slice 3; for now a click
 * navigates to the thread's room.
 */
export function ThreadsListView({ onClose }: ThreadsListViewProps) {
  const navigate = useNavigate()
  const setActiveRoom = useRoomStore((s) => s.setActiveRoom)
  const rooms = useRoomStore((s) => s.rooms)
  // selectAllThreads allocates a fresh sorted array each call; without a
  // shallow compare zustand v5 sees a new reference every render and loops
  // (React #185). useShallow re-renders only when the contents actually change.
  const threads = useThreadStore(useShallow(selectAllThreads))
  const lastReadTs = useThreadStore((s) => s.lastReadTs)

  const open = (info: ThreadInfo) => {
    setActiveRoom(info.roomId)
    navigate(`/room/${encodeURIComponent(info.roomId)}`)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg shadow-lg animate-slide-up max-h-[70vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[rgb(var(--np-border))]">
          <div className="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[rgb(var(--np-tx-secondary))]">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              <line x1="8" y1="9" x2="16" y2="9" />
              <line x1="8" y1="13" x2="13" y2="13" />
            </svg>
            <span className="label-section">Threads</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-[rgb(var(--np-tx-muted))] hover:text-[rgb(var(--np-tx-primary))] transition-colors"
            title="Close threads"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto scroll">
          {threads.length === 0 ? (
            <p className="p-8 text-center text-sm text-[rgb(var(--np-tx-muted))]">
              No threads yet. Replies in a thread will show up here.
            </p>
          ) : (
            threads.map((info) => {
              const unread = threadHasUnread(info, lastReadTs.get(info.rootId) ?? 0)
              const roomName = rooms.get(info.roomId)?.name ?? info.roomId
              return (
                <button
                  key={info.rootId}
                  onClick={() => open(info)}
                  className={`w-full text-left px-4 py-3 border-t border-[rgb(var(--np-border))] hover:bg-[rgb(var(--np-raised))] transition-colors ${unread ? 'bg-[rgb(var(--np-accent)/0.05)]' : ''}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Avatar name={getSenderDisplay(info.rootSender)} userId={info.rootSender} size={20} />
                    <span className="text-xs font-semibold text-[rgb(var(--np-tx-primary))] truncate">{getSenderDisplay(info.rootSender)}</span>
                    <span className="text-[10px] text-[rgb(var(--np-tx-muted))] truncate">in {roomName}</span>
                    {unread && <span className="w-2 h-2 rounded-full bg-[rgb(var(--np-accent))] shrink-0" title="Unread replies" />}
                    <span className="text-[10px] font-data text-[rgb(var(--np-tx-muted))] ml-auto shrink-0">{formatDate(info.latestTs)} {formatTime(info.latestTs)}</span>
                  </div>
                  <p className={`text-sm truncate ${unread ? 'text-[rgb(var(--np-tx-primary))] font-medium' : 'text-[rgb(var(--np-tx-secondary))]'}`}>
                    {info.rootPreview || '\u{1F512} Encrypted'}
                  </p>
                  <div className="flex items-center gap-2 mt-1 text-[10px] text-[rgb(var(--np-tx-muted))]">
                    <span>{info.replyCount} {info.replyCount === 1 ? 'reply' : 'replies'}</span>
                    {info.replyCount > 0 && (
                      <span className="truncate">latest from {getSenderDisplay(info.latestSender)}</span>
                    )}
                  </div>
                </button>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
